import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { axisProps, gridProps, Legend, LINE_CURSOR, makeTooltip, type ChartDatum, type SeriesDef } from './parts';
import { compact, longDate, shortDate } from '../../lib/format';

interface Props {
  data: readonly ChartDatum[];
  series: SeriesDef[];
  /** Area for a single series, line once series must be told apart. */
  variant?: 'area' | 'line';
  height?: number;
  xKey?: string;
  valueFormat?: (n: number) => string;
  /** Ticks every Nth point, so a 90-day axis does not collide. */
  tickEvery?: number;
  /** Widen when the formatted tick is long — "99.75%" clips at the default. */
  yWidth?: number;
  /** Pin the scale when auto-fitting would over-zoom, e.g. a rate near 100%. */
  yDomain?: [number, number];
  /** A budget or floor to draw the series against. */
  reference?: { y: number; label: string };
}

export function TrendChart({
  data,
  series,
  variant = 'line',
  height = 260,
  xKey = 'date',
  valueFormat = compact,
  tickEvery,
  yWidth = 46,
  yDomain,
  reference,
}: Props) {
  const step = tickEvery ?? Math.max(1, Math.ceil(data.length / 8));
  const ticks = data
    .filter((_, i) => i % step === 0)
    .map((d) => String((d as Record<string, unknown>)[xKey]));
  const Tip = makeTooltip(longDate, (v) => valueFormat(v));
  const rows = data as ChartDatum[];

  /*
   * A line through one point draws nothing, so the 1-day range rendered every
   * trend chart as an empty frame. Show the marks themselves at that size — one
   * dot per series is an honest reading of a single day, where a blank panel
   * looks like the data failed to load.
   */
  const sparse = rows.length < 2;
  const dot = sparse ? { r: 3.5, strokeWidth: 0 } : false;

  const common = (
    <>
      <CartesianGrid {...gridProps} />
      <XAxis dataKey={xKey} {...axisProps} ticks={ticks} tickFormatter={shortDate} tickMargin={10} />
      <YAxis
        {...axisProps}
        tickFormatter={valueFormat}
        width={yWidth}
        tickMargin={6}
        domain={yDomain ?? ['auto', 'auto']}
      />
      {reference ? (
        <ReferenceLine
          y={reference.y}
          stroke="var(--status-serious)"
          strokeDasharray="4 4"
          strokeWidth={1.5}
          label={{ value: reference.label, position: 'insideBottomLeft', fill: 'var(--text-muted)', fontSize: 11 }}
        />
      ) : null}
      <Tooltip content={Tip} cursor={LINE_CURSOR} />
    </>
  );

  return (
    <div className="chart-wrap">
      <Legend series={series} />
      <ResponsiveContainer width="100%" height={height}>
        {variant === 'area' ? (
          <AreaChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <defs>
              {series.map((s) => (
                <linearGradient id={`fill-${s.key}`} key={s.key} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
                </linearGradient>
              ))}
            </defs>
            {common}
            {series.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                strokeWidth={2}
                fill={`url(#fill-${s.key})`}
                dot={dot}
                // A 2px surface ring keeps overlapping marks readable.
                activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--surface-1)' }}
                isAnimationActive={false}
              />
            ))}
          </AreaChart>
        ) : (
          <LineChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            {common}
            {series.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                strokeWidth={2}
                dot={dot}
                activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--surface-1)' }}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
